/**
 * SEO helpers — JSON-LD builders for Schema.org rich results.
 *
 * iter53 — The imperative `setSEO()` / `resetSEO()` helpers have been
 * superseded by the declarative `<SEO />` component (react-helmet-async)
 * so per-page tags are captured by the prerender pipeline. The JSON-LD
 * builders below remain shared between the runtime SEO component and
 * potential build-time consumers.
 */

const SITE = "https://discoveur.com";
const DEFAULT_IMAGE = "https://discoveur.com/icon-512.png";

/** Build a Schema.org `Event` JSON-LD from a Discoveur event doc. */
export function buildEventJsonLd(ev) {
    if (!ev) return null;
    const name = ev.title || ev.restaurant_name || "Événement";
    const url = `${SITE}/events/${ev.id}`;
    const location = {
        "@type": "Place",
        name: ev.city || "France",
        address: {
            "@type": "PostalAddress",
            streetAddress: ev.address || "",
            addressLocality: ev.city || "",
            addressCountry: ev.country || "France",
        },
    };
    const startDate = ev.heure_debut && ev.date
        ? `${ev.date}T${ev.heure_debut}:00`
        : ev.date;
    const endDate = ev.end_date || (ev.heure_fin && ev.date
        ? `${ev.date}T${ev.heure_fin}:00`
        : undefined);
    const offers = ev.prix
        ? {
            "@type": "Offer",
            price: ev.prix,
            priceCurrency: "EUR",
            url: ev.lien_reservation || url,
            availability: "https://schema.org/InStock",
        }
        : undefined;
    return {
        "@context": "https://schema.org",
        "@type": "Event",
        name,
        startDate,
        ...(endDate ? { endDate } : {}),
        location,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        description: (ev.description || "").slice(0, 500),
        image: ev.flyer_base64
            ? `${SITE}/api/events/${ev.id}/flyer.jpg`
            : DEFAULT_IMAGE,
        organizer: {
            "@type": "Organization",
            name: ev.organizer_name || "Discoveur",
            url: SITE,
        },
        url,
        ...(offers ? { offers } : {}),
    };
}

/** Build a Schema.org `Article` JSON-LD from a Discoveur POV article. */
export function buildArticleJsonLd(a) {
    if (!a) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: (a.title || "").slice(0, 110),
        description: (a.excerpt || a.preview_text || "").slice(0, 500),
        image: DEFAULT_IMAGE, // per-article images are base64 in Mongo; not linkable
        author: {
            "@type": "Person",
            name: a.author_name || "Discoveur",
        },
        publisher: {
            "@type": "Organization",
            name: "Discoveur",
            logo: {
                "@type": "ImageObject",
                url: `${SITE}/icon-512.png`,
            },
        },
        datePublished: a.created_at,
        dateModified: a.updated_at || a.created_at,
        mainEntityOfPage: `${SITE}/pov/${a.id}`,
    };
}

/** Build a Schema.org `BreadcrumbList` from an array of `{name, path}`. */
export function buildBreadcrumbJsonLd(items) {
    if (!Array.isArray(items) || !items.length) return null;
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: `${SITE}${it.path}`,
        })),
    };
}
